import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Upload,
  CheckCircle,
  Clock,
  AlertCircle,
  TrendingUp,
  History,
  Copy,
  X,
  Loader,
  Info,
} from "lucide-react";
import { sipotmGetStats, sipotmExtractDocument, sipotmGetWorkOrders } from "../api";
import "./SIPOTMDashboard.css";

const SIPOTMDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentWorkOrders, setRecentWorkOrders] = useState([]);
  
  // Extract state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uid, setUid] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [extractionResult, setExtractionResult] = useState(null);
  const [extractionError, setExtractionError] = useState(null);
  const [activeTab, setActiveTab] = useState("metadata");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsData, workOrdersData] = await Promise.all([
        sipotmGetStats().catch(() => null),
        sipotmGetWorkOrders({ limit: 5 }).catch(() => ({ work_orders: [] }))
      ]);
      
      if (statsData) {
        setStats(statsData);
      }
      setRecentWorkOrders(workOrdersData?.work_orders || []);
    } catch (err) {
      console.error("Failed to fetch SIP/OTM data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setExtractionResult(null);
      setExtractionError(null);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
      setExtractionResult(null);
      setExtractionError(null);
    }
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleExtract = async () => {
    if (!selectedFile) {
      setExtractionError("Please select a file to upload");
      return;
    }

    setExtracting(true);
    setExtractionError(null);
    setExtractionResult(null);

    try {
      const result = await sipotmExtractDocument(selectedFile, uid || undefined);
      setExtractionResult(result);
      setActiveTab("metadata");
      // Refresh data after extraction
      fetchData();
    } catch (err) {
      console.error("Extraction failed:", err);
      setExtractionError(err.message || "Extraction failed");
    } finally {
      setExtracting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const clearResult = () => {
    setExtractionResult(null);
    setExtractionError(null);
    setSelectedFile(null);
    setUid("");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "#8b5cf6";
      case "processing": return "#3b82f6";
      case "failed": return "#ef4444";
      default: return "#6b7280";
    }
  };

  const getTabData = () => {
    if (!extractionResult) return {};
    switch (activeTab) {
      case "metadata": return extractionResult.metadata || {};
      case "sip": return extractionResult.sip_data || {};
      case "otm": return extractionResult.otm_data || {};
      case "coords": return extractionResult.field_coordinates || {};
      default: return extractionResult;
    }
  };

  return (
    <div className="sipotm-dashboard">
      {/* Welcome Banner */}
      <div className="sipotm-welcome-banner">
        <div className="sipotm-welcome-content">
          <h1>SIP/OTM Document Extraction</h1>
          <p>Extract SIP registration and OTM (NACH) mandate form data with auto AMC detection, coordinates and confidence scores</p>
        </div>
        <div className="sipotm-welcome-actions">
          <button className="sipotm-welcome-btn primary" onClick={() => navigate("/sip-otm/history")}>
            <History size={20} />
            View All Work Orders
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="sipotm-stats-overview">
        <div className="sipotm-stat-card total">
          <div className="sipotm-stat-icon">
            <FileText size={24} />
          </div>
          <div className="sipotm-stat-info">
            <span className="sipotm-stat-value">{loading ? "-" : stats?.total || 0}</span>
            <span className="sipotm-stat-label">Total Extractions</span>
          </div>
        </div>

        <div className="sipotm-stat-card completed">
          <div className="sipotm-stat-icon">
            <CheckCircle size={24} />
          </div>
          <div className="sipotm-stat-info">
            <span className="sipotm-stat-value">{loading ? "-" : stats?.completed || 0}</span>
            <span className="sipotm-stat-label">Completed</span>
          </div>
        </div>

        <div className="sipotm-stat-card failed">
          <div className="sipotm-stat-icon">
            <AlertCircle size={24} />
          </div>
          <div className="sipotm-stat-info">
            <span className="sipotm-stat-value">{loading ? "-" : stats?.failed || 0}</span>
            <span className="sipotm-stat-label">Failed</span>
          </div>
        </div>

        <div className="sipotm-stat-card today">
          <div className="sipotm-stat-icon">
            <Clock size={24} />
          </div>
          <div className="sipotm-stat-info">
            <span className="sipotm-stat-value">{loading ? "-" : stats?.today || 0}</span>
            <span className="sipotm-stat-label">Today</span>
          </div>
        </div>

        <div className="sipotm-stat-card rate">
          <div className="sipotm-stat-icon">
            <TrendingUp size={24} />
          </div>
          <div className="sipotm-stat-info">
            <span className="sipotm-stat-value">{loading ? "-" : `${stats?.success_rate || 0}%`}</span>
            <span className="sipotm-stat-label">Success Rate</span>
          </div>
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="sipotm-main-grid">
        {/* Left: Upload & Extract */}
        <div className="sipotm-card">
          <div className="sipotm-card-header">
            <Upload size={20} />
            <span>Extract SIP/OTM Document</span>
          </div>
          <div className="sipotm-card-body">
            {/* Info Banner */}
            <div className="sipotm-info-banner">
              <Info size={16} />
              <div>
                <h4>Auto AMC Detection</h4>
                <p>Upload SIP registration or OTM (NACH) mandate forms. AMC name, form type, and metadata are automatically extracted from the document.</p>
              </div>
            </div>

            {/* File Upload Zone */}
            <div 
              className={`sipotm-upload-zone ${selectedFile ? 'has-file' : ''}`}
              onClick={() => document.getElementById('sipotm-file-input').click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              {selectedFile ? (
                <>
                  <FileText size={32} className="sipotm-file-icon" />
                  <p className="sipotm-file-name">{selectedFile.name}</p>
                  <p className="sipotm-file-size">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                </>
              ) : (
                <>
                  <Upload size={32} />
                  <p>Click or drag file here to upload</p>
                  <p className="sipotm-upload-hint">PDF, TIFF, JPG, PNG (max 50MB)</p>
                </>
              )}
            </div>
            <input
              type="file"
              id="sipotm-file-input"
              accept=".pdf,.tiff,.tif,.jpg,.jpeg,.png"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            {/* UID Input */}
            <div className="sipotm-form-group">
              <label htmlFor="sipotm-uid">UID (optional tracking ID)</label>
              <input
                type="text"
                id="sipotm-uid"
                value={uid}
                onChange={(e) => setUid(e.target.value)}
                placeholder="Optional unique identifier"
              />
            </div>

            {/* Extract Button */}
            <button
              className="sipotm-extract-btn"
              onClick={handleExtract}
              disabled={!selectedFile || extracting}
            >
              {extracting ? (
                <>
                  <Loader size={18} className="sipotm-spinner" />
                  Extracting...
                </>
              ) : (
                <>
                  <Upload size={18} />
                  Extract Document
                </>
              )}
            </button>

            {/* Error Display */}
            {extractionError && (
              <div className="sipotm-error-message">
                <AlertCircle size={16} />
                <span>{extractionError}</span>
              </div>
            )}
          </div>
        </div>

        {/* Right: Results */}
        <div className="sipotm-card">
          <div className="sipotm-card-header">
            <TrendingUp size={20} />
            <span>Extraction Results</span>
            {extractionResult && (
              <button className="sipotm-clear-btn" onClick={clearResult}>
                <X size={16} />
              </button>
            )}
          </div>
          <div className="sipotm-card-body">
            {extractionResult ? (
              <div className="sipotm-result-section">
                {/* Result Meta */}
                <div className="sipotm-result-meta">
                  <div className="sipotm-meta-item">
                    <span>AMC:</span>
                    <strong>{extractionResult.metadata?.amc_name || '-'}</strong>
                  </div>
                  <div className="sipotm-meta-item">
                    <span>Form Type:</span>
                    <strong>{extractionResult.form_type || extractionResult.metadata?.form_type || '-'}</strong>
                  </div>
                  <div className="sipotm-meta-item">
                    <span>Work Order:</span>
                    <strong>{extractionResult.work_order_id || '-'}</strong>
                  </div>
                  <div className="sipotm-meta-item">
                    <span>Created:</span>
                    <strong>{extractionResult.created_at || '-'}</strong>
                  </div>
                </div>

                {/* Tabs */}
                <div className="sipotm-result-tabs">
                  <button 
                    className={`sipotm-tab ${activeTab === 'metadata' ? 'active' : ''}`}
                    onClick={() => setActiveTab('metadata')}
                  >
                    Metadata
                  </button>
                  <button 
                    className={`sipotm-tab ${activeTab === 'sip' ? 'active' : ''}`}
                    onClick={() => setActiveTab('sip')}
                  >
                    SIP Data
                  </button>
                  <button 
                    className={`sipotm-tab ${activeTab === 'otm' ? 'active' : ''}`}
                    onClick={() => setActiveTab('otm')}
                  >
                    OTM Data
                  </button>
                  <button 
                    className={`sipotm-tab ${activeTab === 'coords' ? 'active' : ''}`}
                    onClick={() => setActiveTab('coords')}
                  >
                    Coordinates
                  </button>
                  <button 
                    className={`sipotm-tab ${activeTab === 'full' ? 'active' : ''}`}
                    onClick={() => setActiveTab('full')}
                  >
                    Full JSON
                  </button>
                </div>

                {/* Result JSON */}
                <div className="sipotm-result-json-header">
                  <strong>
                    {activeTab === 'metadata' ? 'Metadata (AMC, Form Type, etc.)' : 
                     activeTab === 'sip' ? 'SIP Data' : 
                     activeTab === 'otm' ? 'OTM Data' : 
                     activeTab === 'coords' ? 'Field Coordinates' : 'Full Response'}
                  </strong>
                  <button
                    className="sipotm-copy-btn"
                    onClick={() => copyToClipboard(JSON.stringify(getTabData(), null, 2))}
                  >
                    <Copy size={14} />
                    Copy JSON
                  </button>
                </div>
                <pre className="sipotm-result-json">
                  {JSON.stringify(getTabData(), null, 2)}
                </pre>
              </div>
            ) : (
              <div className="sipotm-no-results">
                <FileText size={48} />
                <p>Upload a document and click Extract to see results</p>
                <p className="sipotm-no-results-hint">AMC, form type, and metadata will be automatically detected</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Work Orders */}
      <div className="sipotm-recent-section">
        <h2 className="sipotm-section-title">
          <History size={20} />
          Recent Extractions
        </h2>
        
        {recentWorkOrders.length > 0 ? (
          <div className="sipotm-recent-list">
            {recentWorkOrders.map((wo) => (
              <div
                key={wo._id || wo.work_order_id}
                className="sipotm-recent-item"
                onClick={() => navigate(`/sip-otm/work-order/${wo._id || wo.work_order_id}`)}
              >
                <div className="sipotm-recent-icon">
                  <FileText size={20} />
                </div>
                <div className="sipotm-recent-info">
                  <div className="sipotm-recent-filename">{wo.filename || wo.uid || 'Document'}</div>
                  <div className="sipotm-recent-meta">
                    <span className="sipotm-recent-amc">{wo.metadata?.amc_name || wo.amc || '-'}</span>
                    <span className="sipotm-recent-type">{wo.form_type || '-'}</span>
                    <span>{wo.created_at && new Date(wo.created_at).toLocaleString()}</span>
                  </div>
                </div>
                <span
                  className="sipotm-recent-status"
                  style={{ backgroundColor: `${getStatusColor(wo.status)}20`, color: getStatusColor(wo.status) }}
                >
                  {wo.status || 'pending'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="sipotm-empty-recent">
            <p>No recent extractions. Upload a document to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SIPOTMDashboard;
